/**
 * GsuiteDirectoryPanel — Google Workspace directory-sync management surface for
 * the shared Google app registration's "Directory / SSO" feature (CD brief
 * addendum #3, kit cb7b5fe4). Renders ONLY when the `directory` feature is
 * enabled; everything is backend-driven through the typed
 * `@v0y4ge/api-client` gsuite/manage ops — no seed/hardcoded state, honest
 * error/empty states, awaited mutations that re-fetch (no-fake-interactivity).
 *
 * Scope note: these ops are Google-Workspace-specific (`/gsuite/manage/*`).
 * Microsoft Entra directory sync is a separate (Graph) surface not shipped in
 * this kit, so this panel is Google-only — the ONE provider branch here is an
 * API-availability gate, not a layout fork of the shared AppRegistration.
 */
import { useEffect, useState } from "react";
import { Btn, Card, Icon, Pill, T, tx } from "@v0y4ge/jrni-ui";
import {
  getGsuiteRoster, getGsuiteStaffCalendar, getGsuiteStaffGmail,
  setGsuiteStaffOoo, clearGsuiteStaffOoo, verifyGsuiteStep, provisionGsuite,
} from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";

interface RosterStaff { staffId: string; displayName?: string; email?: string; calendarId?: string; isLoginUser?: boolean }
const unwrap = (r: unknown): any => (r && typeof r === "object" && "data" in (r as any) ? (r as any).data : r);
const str = (v: unknown): string => (v == null ? "" : String(v));

/* One expanded staff member: synced calendar busy/free + recent Gmail + OOO. */
function StaffDetail({ staff, canWrite }: { staff: RosterStaff; canWrite: boolean }) {
  const t = useT();
  const [cal, setCal] = useState<any[] | null>(null);
  const [mail, setMail] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setErr(null);
    try {
      const [c, m] = await Promise.all([
        getGsuiteStaffCalendar(staff.staffId, {}).then(unwrap).catch(() => ({ items: [] })),
        getGsuiteStaffGmail(staff.staffId).then(unwrap).catch(() => ({ messages: [] })),
      ]);
      setCal(Array.isArray(c?.items) ? c.items : []);
      setMail(Array.isArray(m?.messages) ? m.messages : []);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [staff.staffId]);
  const setOoo = async () => {
    if (busy || !canWrite) return;
    setBusy(true);
    const today = new Date().toISOString().slice(0, 10);
    const end = new Date(Date.now() + 2 * 864e5).toISOString().slice(0, 10);
    try {
      await setGsuiteStaffOoo(staff.staffId, { kind: "ooo", startDate: today, endDate: end });
      toast({ tone: "success", title: t("settings.integrationDetail.oooAdded", "Out-of-office added") });
      await load();
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.oooFailed", "Couldn't set out-of-office"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const clearOoo = async (eventId: string) => {
    if (busy || !canWrite) return;
    setBusy(true);
    try {
      await clearGsuiteStaffOoo(staff.staffId, eventId);
      toast({ tone: "success", title: t("settings.integrationDetail.oooCleared", "Out-of-office cleared") });
      await load();
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.oooClearFailed", "Couldn't clear out-of-office"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const oooEvent = (cal ?? []).find((it) => /ooo|sick|pto|out.of.office/i.test(str(it?.summary || it?.title || it?.kind)));
  return (
    <div style={{ padding: "10px 16px 14px 54px", background: `color-mix(in srgb, ${T.p1} 3%, ${T.white})`, borderTop: `1px solid ${T.border}` }}>
      {err ? <div style={{ fontSize: 12, color: T.red1 }}>{err}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <div style={{ ...tx.label as any, marginBottom: 6 }}>{t("settings.integrationDetail.syncedCalendar", "Synced calendar")}</div>
          {cal == null ? <div style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loading", "Loading…")}</div>
            : cal.length === 0 ? <div style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.noBusyBlocks", "No busy/free blocks synced.")}</div>
            : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {cal.slice(0, 6).map((it, i) => (
                  <li key={i} style={{ fontSize: 12, color: T.n1, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{str(it?.summary || it?.title || t("settings.integrationDetail.busy", "Busy"))}</span>
                    <span style={{ color: T.soft, flexShrink: 0 }}>{str(it?.start?.dateTime || it?.start?.date || it?.start || "").slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
        <div>
          <div style={{ ...tx.label as any, marginBottom: 6 }}>{t("settings.integrationDetail.recentGmail", "Recent Gmail")}</div>
          {mail == null ? <div style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loading", "Loading…")}</div>
            : mail.length === 0 ? <div style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.noMessages", "No messages.")}</div>
            : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {mail.slice(0, 5).map((m, i) => (
                  <li key={i} style={{ fontSize: 12, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ fontWeight: 600 }}>{str(m?.from || m?.sender || "")}</span> {str(m?.subject || m?.snippet || "")}
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
      {canWrite ? (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {oooEvent ? (
            <Btn variant="secondary" size="sm" disabled={busy} onClick={() => { void clearOoo(str(oooEvent.id || oooEvent.eventId)); }}>{t("settings.integrationDetail.clearOoo", "Clear out-of-office")}</Btn>
          ) : (
            <Btn variant="secondary" size="sm" disabled={busy} onClick={() => { void setOoo(); }}>{t("settings.integrationDetail.setOoo", "Set out-of-office")}</Btn>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function GsuiteDirectoryPanel({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const [roster, setRoster] = useState<{ configured: boolean; staff: RosterStaff[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "provision" | "verify">(null);
  const load = async () => {
    setErr(null);
    try {
      const d = unwrap(await getGsuiteRoster());
      setRoster({ configured: !!d?.configured, staff: Array.isArray(d?.staff) ? d.staff : [] });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setRoster({ configured: false, staff: [] }); }
  };
  useEffect(() => { void load(); }, []);
  const provision = async () => {
    if (busy || !canWrite) return;
    setBusy("provision");
    try {
      const r = unwrap(await provisionGsuite());
      toast({ tone: r?.ok === false ? "warn" : "success", title: r?.ok === false ? t("settings.integrationDetail.provisionFailed", "Provisioning failed") : t("settings.integrationDetail.provisioned", "Workspace directory provisioned"), subtitle: r?.rosterCount != null ? `${r.rosterCount} staff synced` : undefined });
      await load();
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.provisionFailed", "Provisioning failed"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(null); }
  };
  const verify = async () => {
    if (busy) return;
    setBusy("verify");
    try {
      const r = unwrap(await verifyGsuiteStep({ step: "verify-calendar" }));
      toast({ tone: r?.ok ? "success" : "warn", title: r?.ok ? t("settings.integrationDetail.calendarVerified", "Calendar access verified") : t("settings.integrationDetail.calendarVerifyFailed", "Calendar verification failed"), subtitle: r?.detail ? String(r.detail) : undefined });
    } catch (e) { toast({ tone: "warn", title: t("settings.integrationDetail.calendarVerifyFailed", "Calendar verification failed"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(null); }
  };
  const staff = roster?.staff ?? [];
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.directorySync", "Directory sync")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.directorySyncSub", "Staff synced from your Google Workspace — calendars, out-of-office and mail.")}</p>
        </div>
        {roster?.configured ? (
          <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={busy != null} onClick={() => { void verify(); }}>{busy === "verify" ? t("settings.integrationDetail.verifying", "Verifying…") : t("settings.integrationDetail.verify", "Verify")}</Btn>
        ) : null}
      </div>
      {err ? <div style={{ padding: 16, fontSize: 12, color: T.red1 }}>{err}</div> : null}
      {roster == null ? (
        <div style={{ padding: 20, fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loadingRoster", "Loading roster…")}</div>
      ) : !roster.configured || staff.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{t("settings.integrationDetail.directoryNotSynced", "Directory not synced yet")}</div>
          <div style={{ fontSize: 12, color: T.soft, margin: "4px 0 12px", lineHeight: 1.5 }}>{t("settings.integrationDetail.directoryNotSyncedSub", "Enable Directory to sync staff, calendars and mail from your Google Workspace.")}</div>
          {canWrite ? <Btn variant="secondary" size="sm" disabled={busy != null} onClick={() => { void provision(); }}>{busy === "provision" ? t("settings.integrationDetail.provisioning", "Provisioning…") : t("settings.integrationDetail.provisionNow", "Provision now")}</Btn> : null}
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {staff.map((s, i) => {
            const isOpen = open === s.staffId;
            return (
              <li key={s.staffId} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer" }} onClick={() => setOpen(isOpen ? null : s.staffId)}>
                  <span style={{ width: 28, height: 28, borderRadius: 999, background: T.n6, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{str(s.displayName || s.email || "?").charAt(0).toUpperCase()}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{str(s.displayName || s.email)}</span>
                      {s.isLoginUser ? <Pill tone="info">{t("settings.integrationDetail.loginUser", "Login user")}</Pill> : null}
                    </div>
                    <div style={{ fontSize: 12, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{str(s.email)}</div>
                  </div>
                  <span style={{ color: T.soft, display: "inline-flex", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}><Icon.chev size={12} dir="right" /></span>
                </div>
                {isOpen ? <StaffDetail staff={s} canWrite={canWrite} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
